import { useState, useRef, useCallback } from 'react';
import _ from 'lodash';
import cn from 'classnames';
import { confirmAlert } from 'react-confirm-alert';

import './AllBrands.scss';
import { useDepartments, useIndustries } from './AllBrands.hooks';
import AllBrandsData from './AllBrandsData';
import AllBrandsFilters from './AllBrandsFilters';
import AllBrandsResult from './AllBrandsResult';
import AllBrandsEditResult from './AllBrandsEditResult';
import AllBrandsMergeModal from './AllBrandsMergeModal';
import { addAllBrand, archiveAllBrand, getAllBrandsByIds, mergeAllBrands, updateAllBrand } from '../../api/allbrands';

const NEW_BRAND_ID = 0;
const numberFormatter = new Intl.NumberFormat();
const CATALOG_SYNC_RECONCILE_NOTE = 'the next catalog sync will reconcile search. Retry, or check logs if results persist.';

const AllBrands = () => {
  // Data
  const [curSearchVal, setCurSearchVal] = useState('');
  const [page, setPage] = useState(0);
  const [curFilters, setCurFilters] = useState([]);
  const [results, setResults] = useState([]);
  const [brandDataConflicts, setBrandDataConflicts] = useState({});
  const [isFetching, setIsFetching] = useState(false);
  const [hasAllResults, setHasAllResults] = useState(false);
  const [totals, setTotals] = useState({ total: 0, incomplete: 0 });
  const isFetchingRef = useRef(false); // Used to prevent multiple fetches on scroll
  const isFetchingFirstPage = isFetching && page === 0;
  const isFetchingNextPage = isFetching && page > 0;

  // Editing
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [brandEdits, setBrandEdits] = useState({});
  const [isSavingBrand, setIsSavingBrand] = useState({});
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const { isFetching: isFetchingIndustries, industries } = useIndustries();
  const { isFetching: isFetchingDepartments, departments } = useDepartments();
  const [filtersRefreshKey, setFiltersRefreshKey] = useState(0);
  const [searchRefreshKey, setSearchRefreshKey] = useState(0);
  const editBrand = useCallback((brandId, edits) => {
    setBrandEdits(prev => ({ ...prev, [brandId]: { ...(prev[brandId] ?? {}), ...edits } }));
  }, []);
  const cancelEdit = brandId => {
    setBrandEdits(prev => _.omit(prev, brandId));
    setBrandDataConflicts(prev => _.omit(prev, brandId));
  };
  const cancelNewBrand = () => {
    cancelEdit(NEW_BRAND_ID);
    setIsAdding(false);
  };
  const saveEdit = async (brandId, force) => {
    setIsSavingBrand(prev => ({ ...prev, [brandId]: true }));
    try {
      const { name, domains, logo, industryId, industryName, departments, hideCrossBrandRecommendations } = brandEdits[brandId];
      const { success, conflicts } = await updateAllBrand(brandId, {
        name: name === '' ? null : name,
        domains: domains ?? undefined,
        logo: logo === '' ? null : logo,
        Industry_id: industryId === 0 ? null : industryId,
        departmentIds: departments?.length ? _.map(departments, 'Department_id') : departments?.length === 0 ? null : undefined,
        hideCrossBrandRecommendations,
        force
      });
      if (success) {
        setResults(prev =>
          prev.map(brand => {
            if (brand.id === brandId) {
              const updatedDomains = domains ?? brand.domains;
              return {
                ...brand,
                name: name ?? brand.name,
                domain: updatedDomains?.find(d => d.isPrimary)?.domain ?? brand.domain,
                altDomains: updatedDomains?.filter(d => !d.isPrimary).map(d => d.domain) ?? brand.altDomains,
                domains: updatedDomains ?? brand.domains,
                logo: logo ?? brand.logo,
                industryId: industryId === 0 ? industryId : industryId ? industryId : brand.industryId,
                industryName: industryName ? industryName : brand.industryName,
                departments: departments ? departments : brand.departments,
                hideCrossBrandRecommendations: hideCrossBrandRecommendations ?? brand.hideCrossBrandRecommendations
              };
            }
            return brand;
          })
        );
        cancelEdit(brandId);
        setFiltersRefreshKey(prev => prev + 1);
      } else if (conflicts?.length) {
        setBrandDataConflicts(prev => ({ ...prev, [brandId]: conflicts }));
      } else {
        window.ALERT.error(`There was an issue saving edits for ${brandId}.`);
      }
    } catch (error) {
      console.error(error);
      window.ALERT.error(`There was an issue saving edits for ${brandId}.`);
    } finally {
      setIsSavingBrand(prev => ({ ...prev, [brandId]: false }));
    }
  };
  const addNewBrand = () => {
    setIsAdding(true);
  };
  const saveNewBrand = async () => {
    setIsSavingBrand(prev => ({ ...prev, [NEW_BRAND_ID]: true }));
    try {
      const { name, domains, logo, industryId, departments } = brandEdits[NEW_BRAND_ID];
      const { success, conflicts } = await addAllBrand({
        name: name === '' ? null : name,
        domains: domains ?? undefined,
        logo: logo === '' ? null : logo,
        Industry_id: industryId === 0 ? null : industryId,
        departmentIds: departments?.length ? _.map(departments, 'Department_id') : departments?.length === 0 ? null : undefined
      });
      if (success) {
        cancelNewBrand();
        window.ALERT.success('New brand created');
        setSearchRefreshKey(prev => prev + 1);
      } else if (conflicts?.length) {
        setBrandDataConflicts(prev => ({ ...prev, [NEW_BRAND_ID]: conflicts }));
      } else {
        window.ALERT.error('There was an issue saving new brand.');
      }
    } catch (error) {
      console.error(error);
      window.ALERT.error('There was an issue saving new brand.');
    } finally {
      setIsSavingBrand(prev => ({ ...prev, [NEW_BRAND_ID]: false }));
    }
  };
  const saveAll = async () => {
    await Promise.all(
      Object.keys(brandEdits)
        .filter(id => id !== NEW_BRAND_ID)
        .map(brandId => saveEdit(Number(brandId)))
    );
  };

  // Merging
  const [isMerging, setIsMerging] = useState(false);
  const [isSavingMerge, setIsSavingMerge] = useState(false);
  const [isMergedNameConflicting, setIsMergedNameConflicting] = useState(false);
  const mergeBrands = async () => {
    setIsMerging(true);
  };
  const selectAndMerge = async brandIds => {
    const [loadedBrandIds, unloadedBrandIds] = _.partition(brandIds, brandId => results.some(brand => brand.id === brandId));

    const loadedBrands = results.filter(brand => loadedBrandIds.includes(brand.id));
    if (unloadedBrandIds.length) {
      const missingBrands = await getAllBrandsByIds([['ids', unloadedBrandIds]]);
      loadedBrands.push(...missingBrands.brands);
    }
    setSelectedBrands(loadedBrands);
    setIsMerging(true);
  };
  const saveMerge = async data => {
    setIsSavingMerge(true);
    try {
      const { success } = await mergeAllBrands({ ...data, ids: selectedBrands.map(brand => brand.id) });
      if (success) {
        setIsMerging(false);
        window.ALERT.success('Brands merged');
        setSelectedBrands([]);
        setBrandDataConflicts(prev =>
          _.omit(
            prev,
            selectedBrands.map(brand => brand.id)
          )
        );
        setSearchRefreshKey(prev => prev + 1);
      } else {
        setIsMergedNameConflicting(true);
      }
    } catch (error) {
      console.error(error);
      window.ALERT.error('There was an issue merging brands.');
    } finally {
      setIsSavingMerge(false);
    }
  };
  const cancelMerge = () => {
    setIsMerging(false);
  };

  // Archiving
  const archiveSelectedBrands = () => {
    const brandNames = selectedBrands.map(b => b.name || `ID ${b.id}`).join(', ');
    confirmAlert({
      title: 'Archive Brands',
      message: `Archive ${selectedBrands.length} brand${
        selectedBrands.length > 1 ? 's' : ''
      } (${brandNames})? This will hide all associated products.`,
      buttons: [
        {
          label: 'Cancel',
          className: 'cancel'
        },
        {
          label: 'Hide from Catalog Only',
          onClick: () => executeArchive({ setIsHidden: false, setIsHiddenFromPublicCatalog: true })
        },
        {
          label: 'Fully Hide Products',
          onClick: () => executeArchive({ setIsHidden: true, setIsHiddenFromPublicCatalog: true })
        }
      ]
    });
  };
  const executeArchive = async options => {
    const promises = selectedBrands.map(brand => archiveAllBrand(brand.id, { isArchived: true, ...options }));
    try {
      const results = await Promise.all(promises);
      const totalProducts = _.sumBy(results, 'affectedProductCount');
      const totalCatalogObjects = _.sumBy(results, result => result.affectedAlgoliaObjectCount || 0);
      const catalogSyncErrorCount = results.filter(result => result.catalogSyncError).length;
      const archiveMessage = `Archived ${selectedBrands.length} brand${
        selectedBrands.length > 1 ? 's' : ''
      }. ${totalProducts} products and ${totalCatalogObjects} catalog search results hidden.`;
      if (catalogSyncErrorCount) {
        window.ALERT.warn(
          `${archiveMessage} Catalog search sync failed for ${catalogSyncErrorCount} brand${
            catalogSyncErrorCount === 1 ? '' : 's'
          } — products are hidden in the database and ${CATALOG_SYNC_RECONCILE_NOTE}`
        );
      } else {
        window.ALERT.success(archiveMessage);
      }
      setSelectedBrands([]);
      setSearchRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error(error);
      window.ALERT.error('There was an issue archiving brands.');
    }
  };
  const unarchiveBrand = brandId => {
    const brand = results.find(b => b.id === brandId);
    confirmAlert({
      title: 'Unarchive Brand',
      message: `Unarchive ${brand?.name || `ID ${brandId}`}?`,
      buttons: [
        {
          label: 'Cancel',
          className: 'cancel'
        },
        {
          label: 'Unarchive Only',
          onClick: () => executeUnarchive(brandId, false)
        },
        {
          label: 'Unarchive & Restore Products',
          onClick: () => executeUnarchive(brandId, true)
        }
      ]
    });
  };
  const executeUnarchive = async (brandId, restoreProducts) => {
    try {
      const { affectedProductCount, affectedAlgoliaObjectCount = 0, catalogSyncError } = await archiveAllBrand(brandId, { isArchived: false, restoreProducts });
      const msg = restoreProducts
        ? `Brand unarchived. ${affectedProductCount} products and ${affectedAlgoliaObjectCount} catalog search results restored.`
        : 'Brand unarchived. Products left as-is.';
      if (catalogSyncError) {
        window.ALERT.warn(`${msg} Catalog search sync failed — the change is saved in the database and ${CATALOG_SYNC_RECONCILE_NOTE}`);
      } else {
        window.ALERT.success(msg);
      }
      setSearchRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error(error);
      window.ALERT.error('There was an issue unarchiving the brand.');
    }
  };

  // Sorting
  const tableColumns = isEditing ? ['Brand', 'Industry', 'Tags', 'Promoters'] : ['Brand', 'Industry', 'Promoters', 'Tags', 'Rankings'];
  const [sortBy, setSortBy] = useState('Promoters');
  const [sortDir, setSortDir] = useState('desc');

  // Scrolling
  const scrollContainerRef = useRef();
  const scrollToTopOfResults = () => {
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  };
  const handleScroll = () => {
    const { scrollHeight, scrollTop, clientHeight } = scrollContainerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    if (distanceFromBottom < 250) {
      if (hasAllResults || isFetchingRef.current) return;
      setPage(page + 1);
    }
  };

  // Loading
  const getLoadingRow = idx => <div key={idx} className={'all-brands-loading-row'} />;

  // Actions
  const changeSearch = newValue => {
    setCurSearchVal(newValue);
    setCurFilters([]);
    setPage(0);
    setHasAllResults(false);
    scrollToTopOfResults();
  };

  const changeSortBy = column => {
    setSortBy(column);
    setHasAllResults(false);
    setPage(0);

    // If clicking twice, change direction, otherwise default desc
    const isClickingTwice = sortBy === column;
    isClickingTwice ? setSortDir(sortDir === 'desc' ? 'asc' : 'desc') : setSortDir('desc');
  };

  const selectedBrandIds = _.keyBy(selectedBrands, 'id');
  const selectBrand = brandId => {
    setSelectedBrands(prev => {
      if (prev.find(b => b.id === brandId)) {
        return prev.filter(b => b.id !== brandId);
      }
      return [...prev, results.find(b => b.id === brandId)];
    });
  };
  const selectAll = () => {
    setAllSelected(prev => {
      if (prev) setSelectedBrands([]);
      else setSelectedBrands(results);
      return !prev;
    });
  };

  const focusNextField = () => {
    const focusedEle = document.activeElement;
    const searchClass = focusedEle.id.includes('name') ? '.all-brands-results-name' : '.all-brands-results-domains';
    const inputsOfType = Array.from(document.querySelectorAll(searchClass).values());
    const currentIndex = inputsOfType.findIndex(f => f.id === focusedEle.id);
    if (currentIndex === -1 || currentIndex === inputsOfType.length - 1) return;
    inputsOfType[currentIndex + 1].focus();
  };

  const editResults = [...selectedBrands, ...results.filter(brand => !selectedBrands.find(b => b.id === brand.id))];

  const showLoaders = (isFetchingFirstPage || isFetchingIndustries || isFetchingDepartments) && !results.length;
  const saveAllDisabled = Object.keys(brandEdits).length === 0;

  return (
    <div
      className='all-brands-outer'
      onKeyUp={e => {
        if (e.key === 'Enter') {
          focusNextField();
        }
      }}
    >
      <h1 className={cn('all-brands-title', isFetchingFirstPage && 'all-brands-title--stale')}>
        {isFetchingFirstPage && !totals?.total ? 'Loading' : numberFormatter.format(totals.total)} Brands
      </h1>
      <div className='all-brands-search'>
        <input
          className='all-brands-search-input'
          type='text'
          value={curSearchVal}
          placeholder='Search by name, domain, or ID'
          onChange={e => changeSearch(e.target.value)}
        />
        {curSearchVal && (
          <button className='all-brands-search-clear' onClick={() => changeSearch('')}>
            𝘅
          </button>
        )}
      </div>
      <div className='all-brands-actions'>
        <label>
          <input type='checkbox' checked={isEditing} onChange={e => setIsEditing(e.target.checked)} />
          <span className='all-brands-edit-toggle'>Edit Results</span>
        </label>
        {isEditing && (
          <>
            <button className='all-brands-add-brand' onClick={() => addNewBrand()}>
              Add Brand
            </button>
            <button
              className={cn('all-brands-merge', selectedBrands.length < 2 && 'all-brands-merge--disabled')}
              disabled={selectedBrands.length < 2}
              onClick={() => mergeBrands()}
            >
              Merge Brands
            </button>
            <button
              className={cn('all-brands-archive', !selectedBrands.length && 'all-brands-archive--disabled')}
              disabled={!selectedBrands.length}
              onClick={() => archiveSelectedBrands()}
            >
              Archive
            </button>
            <button
              className={cn('all-brands-save-all', saveAllDisabled && 'all-brands-save-all--disabled')}
              disabled={saveAllDisabled}
              onClick={() => saveAll()}
            >
              Save All
            </button>
          </>
        )}
      </div>
      <div className='all-brands-filters'>
        <AllBrandsFilters key={filtersRefreshKey} query={curSearchVal} filters={curFilters} setFilters={setCurFilters} />
      </div>
      <div
        ref={scrollContainerRef}
        className={cn('all-brands-results', isFetchingFirstPage && 'all-brands-results-row--stale')}
        onScroll={handleScroll}
      >
        <div className='all-brands-results-header'>
          {isEditing ? <input type='checkbox' checked={allSelected} onChange={() => selectAll()} /> : <div />}
          {tableColumns.map(column => (
            <h3 key={column} onClick={() => changeSortBy(column)}>
              {column} {sortBy === column ? (sortDir === 'desc' ? '↓' : '↑') : ''}
            </h3>
          ))}
        </div>
        {isEditing && isAdding && (
          <AllBrandsEditResult
            brand={{ id: NEW_BRAND_ID, numPromoting: 0, ...(brandEdits[NEW_BRAND_ID] ?? {}) }}
            industries={industries}
            departments={departments}
            conflicts={brandDataConflicts[NEW_BRAND_ID] ?? []}
            hasEdits={true}
            isSelected={true}
            isSaving={Boolean(isSavingBrand[NEW_BRAND_ID])}
            isNewBrand={true}
            onSelect={() => {}}
            onEdit={editBrand}
            onCancel={cancelNewBrand}
            onSave={saveNewBrand}
            onMerge={selectAndMerge}
          />
        )}
        {showLoaders
          ? _.range(4).map(getLoadingRow)
          : isEditing
          ? editResults.map(brand => (
              <AllBrandsEditResult
                key={brand.id}
                brand={{ ...brand, ...(brandEdits[brand.id] ?? {}) }}
                industries={industries}
                departments={departments}
                conflicts={brandDataConflicts[brand.id] ?? []}
                hasEdits={Boolean(brandEdits[brand.id])}
                isSelected={Boolean(selectedBrandIds[brand.id])}
                isSaving={Boolean(isSavingBrand[brand.id])}
                isNewBrand={false}
                onSelect={selectBrand}
                onEdit={editBrand}
                onCancel={cancelEdit}
                onSave={saveEdit}
                onMerge={selectAndMerge}
                onUnarchive={unarchiveBrand}
              />
            ))
          : results.map(brand => (
              <AllBrandsResult key={brand.id} brand={brand} selected={Boolean(selectedBrandIds[brand.id])} onSelect={selectBrand} />
            ))}
        {isFetchingNextPage && getLoadingRow()}
      </div>
      {isMerging && (
        <AllBrandsMergeModal
          brands={selectedBrands}
          isSaving={isSavingMerge}
          isConflicting={isMergedNameConflicting}
          onSave={data => saveMerge(data)}
          onCancel={() => cancelMerge()}
        />
      )}
      <AllBrandsData
        key={searchRefreshKey}
        setIsFetching={setIsFetching}
        isFetchingRef={isFetchingRef}
        page={page}
        curFilters={curFilters}
        sortBy={sortBy}
        sortDir={sortDir}
        isFetching={isFetching}
        results={results}
        setResults={setResults}
        totals={totals}
        setTotals={setTotals}
        curSearchVal={curSearchVal}
        hasAllResults={hasAllResults}
        setHasAllResults={setHasAllResults}
      />
    </div>
  );
};

export default AllBrands;
